import { type AreaResult, COVERAGE_RISK_MAX, coverageNudge, type CoverageNudge } from '@dc1/shared';
import type { TeamCoverage, TeamExperts } from '../../api/queries';
import type { ContentView } from '../../lib/content';

/** The filters above the coverage tiles (design v3). */
export const COVER_FILTERS = ['all', 'risk', 'mine', 'soon'] as const;
export type CoverFilter = (typeof COVER_FILTERS)[number];

/** A tile shows at most this many people; more read "+n". */
export const TILE_PEOPLE = 4;
/** People an area needs so it is no longer a risk. */
export const COVERED_AT = COVERAGE_RISK_MAX + 1;

/** One area as the coverage page shows it to the viewer. */
export interface AreaCover {
  areaId: string;
  groupId: string;
  /** People confirmed at L4+ (the viewer included when they are). */
  count: number;
  /** 0–1 people: nobody covers when that person is away. */
  risk: boolean;
  /** The area applies to nobody in the team (an optional area everyone marked N/A). */
  unused: boolean;
  /** The viewer counts towards it. */
  mine: boolean;
  /** The viewer is one step from covering a risk area: a pending confirmation, or one level short. */
  soon: Extract<CoverageNudge, 'pending' | 'next'> | null;
  /** The viewer's own area result (their levels, their focus). */
  own: AreaResult | undefined;
}

/** Every area in content order, with the viewer's part in it. */
export function coverInfo(
  content: ContentView,
  areas: readonly AreaResult[],
  coverage: TeamCoverage['coverage'],
): AreaCover[] {
  const byArea = new Map(coverage.map((c) => [c.areaId, c]));
  const own = new Map(areas.map((a) => [a.areaId, a]));
  return content.groups.flatMap((g) =>
    g.areas.map((area) => {
      const c = byArea.get(area.id);
      const a = own.get(area.id);
      const nudge = a && c ? coverageNudge(a, c) : null;
      return {
        areaId: area.id,
        groupId: g.id,
        count: c?.count ?? 0,
        risk: c?.risk ?? false,
        unused: (c?.applicableFor ?? 0) === 0,
        mine: nudge === 'covered',
        soon: nudge === 'pending' || nudge === 'next' ? nudge : null,
        own: a,
      };
    }),
  );
}

export function matches(area: AreaCover, filter: CoverFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'soon') return area.soon !== null;
  return area[filter];
}

/** The area a filter opens on: the first match, risk areas first when nothing is filtered. */
export function firstOf(list: readonly AreaCover[], filter: CoverFilter): AreaCover | undefined {
  if (filter === 'all') return list.find((a) => a.risk) ?? list[0];
  return list.find((a) => matches(a, filter)) ?? list[0];
}

export type Slot = 'person' | 'me' | 'missing';

/**
 * The squares of a tile: one per person at L4+ (the viewer's own marked, shown first), up to TILE_PEOPLE,
 * then dashed "missing" squares until the area would stop being a risk.
 */
export function tileSlots(area: Pick<AreaCover, 'count' | 'mine'>, cap = TILE_PEOPLE): Slot[] {
  const people: Slot[] = Array.from({ length: Math.min(area.count, cap) }, (_, i) =>
    i === 0 && area.mine ? 'me' : 'person',
  );
  const missing: Slot[] = Array.from({ length: Math.max(0, COVERED_AT - people.length) }, () => 'missing');
  return [...people, ...missing];
}

/** Who to ask about an area, for the viewer: names when they need it (ADR 0009), else only how many. */
export interface AskWho {
  /** The area is one the viewer needs: below their Level's target, or picked as focus. */
  need: boolean;
  /** People who offered to be asked (never the viewer). */
  names: string[];
  /** Others confirmed at L4+ who have not offered: a count, no names. */
  quiet: number;
}

export function askWho(area: AreaCover, experts: TeamExperts | undefined, meId: string): AskWho {
  const entry = experts?.areas.find((a) => a.areaId === area.areaId);
  const names = entry ? entry.people.filter((p) => p.id !== meId).map((p) => p.displayName) : [];
  const others = area.count - (area.mine ? 1 : 0);
  return { need: entry !== undefined, names, quiet: Math.max(0, others - names.length) };
}

/** Area id → names of the people to ask, without the viewer (who cannot ask themselves). */
export function askWhoByArea(experts: TeamExperts, meId: string): Map<string, string[]> {
  return new Map(
    experts.areas.map((a) => [a.areaId, a.people.filter((p) => p.id !== meId).map((p) => p.displayName)]),
  );
}
